import React, {
  createContext,
  ReactChild,
  ReactElement,
  useEffect,
  useReducer,
  useState
} from 'react';
import { TranslateFunction } from 'react-utilities';
import { DismissOption, PromptType, ViewType } from '../constants/parameters';
import { getPersonalizedResources } from '../constants/resources';
import { EventService } from '../services/eventService';
import { PreferenceService } from '../services/preferenceService';
import { RequestService } from '../services/requestService';
import { AccountSecurityPromptAction } from './action';
import ModalState from './modalState';
import { AccountSecurityPromptState } from './state';
import { runModalStateChangeEffect, runPromptDismissEffect } from './stateEffects';
import accountSecurityPromptStateReducer from './stateReducer';

export type AccountSecurityPromptContext = {
  state: AccountSecurityPromptState;
  dispatch: React.Dispatch<AccountSecurityPromptAction>;
};

/**
 * A React `Context` is global state maintained for some subtree of the React
 * component hierarchy. This particular context is used for the entire
 * `accountSecurityPrompt` web app, containing both the app's state as well
 * as a function to dispatch actions on the state.
 */
export const AccountSecurityPromptContext = createContext<AccountSecurityPromptContext | null>(
  // The argument passed to `createContext` is supposed to define a default
  // value that gets used if no provider is available in the component tree at
  // the time that `useContext` is called. To avoid runtime errors as a result
  // of forgetting to wrap a subtree with a provider, we use `null` as the
  // default value and test for it whenever global state is accessed.
  null
);

type Props = {
  promptType: PromptType;
  viewType: ViewType;
  dismissOption: DismissOption;
  isUserUnder13: boolean;
  eventService: EventService;
  preferenceService: PreferenceService;
  requestService: RequestService;
  translate: TranslateFunction;
  children: ReactChild;
};

/**
 * A React provider is a special component that wraps a tree of components and
 * exposes some global state (context) to the entire tree. Descendants can then
 * access this context with `useContext`.
 */
export const AccountSecurityPromptContextProvider = ({
  promptType,
  viewType,
  dismissOption,
  isUserUnder13,
  eventService,
  preferenceService,
  requestService,
  translate,
  children
}: Props): ReactElement => {
  let modalState = ModalState.NONE;
  if (viewType === ViewType.POP_UP) {
    if (promptType === PromptType.ADD_EMAIL) {
      modalState = ModalState.ADD_EMAIL_FORM;
    } else {
      modalState = ModalState.CHANGE_PASSWORD_INTRO;
    }
  }

  // We declare these variables as lazy-initialized state variables since they
  // do not need to be re-computed if this component re-renders.
  const [resources] = useState(() => getPersonalizedResources(translate, isUserUnder13));
  const [initialState] = useState<AccountSecurityPromptState>(() => ({
    // Immutable parameters:
    promptType,
    viewType,
    dismissOption,

    // Immutable state:
    resources,
    requestService,

    // Mutable state:
    isPromptDismissed: preferenceService.isPromptDismissed(),
    wasPromptDismissedTemporary: false,
    wasPromptDismissedForever: false,
    isFlowComplete: false,
    modalState,
    lastModalState: null,
    emailAddress: '',
    accountPinUnlockedUntil: null
  }));

  // Components will access and mutate state via these variables:
  const [state, dispatch] = useReducer(accountSecurityPromptStateReducer, initialState);

  /*
   * Effects
   *
   * NOTE: These effects cannot go inside the reducer, since reducers must not
   * have side-effects with respect to the app state.
   */

  useEffect(() => runModalStateChangeEffect(eventService, state.modalState, state.lastModalState), [
    eventService,
    state.modalState,
    state.lastModalState
  ]);

  useEffect(
    () =>
      runPromptDismissEffect(
        eventService,
        preferenceService,
        state.wasPromptDismissedTemporary,
        state.wasPromptDismissedForever
      ),
    [
      eventService,
      preferenceService,
      state.wasPromptDismissedTemporary,
      state.wasPromptDismissedForever
    ]
  );

  return (
    <AccountSecurityPromptContext.Provider value={{ state, dispatch }}>
      {children}
    </AccountSecurityPromptContext.Provider>
  );
};
